import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../api/hooks/useColors";
import ContainedButton from "../../buttons/ContainedButton";
import TextButton from "../../buttons/TextButton";
import ToggleSwitch from "../../ToggleSwitch";
import FilterDisplayBox from "./FilterDisplayBox";

const BaseFilterModal = ({
  open,
  onClose,
  onSave,
  title,
  saveButtonText = "Save",
  appliedFilters,
  existingFilter = null,
  isLoading = false,
}) => {
  const colors = useColors();
  const [filterName, setFilterName] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [showAsterisk, setShowAsterisk] = useState(true);

  // Hide asterisk when user starts typing in filter name
  useEffect(() => {
    setShowAsterisk(filterName.trim() === "");
  }, [filterName]);

  // Initialize form with existing filter data (for edit mode)
  useEffect(() => {
    if (existingFilter) {
      setFilterName(existingFilter.name || "");
      setFilterDescription(existingFilter.description || "");
      setSetAsDefault(existingFilter.is_default || false);
    } else {
      // Reset form for new filter
      setFilterName("");
      setFilterDescription("");
      setSetAsDefault(false);
    }
  }, [existingFilter, open]);

  const handleSave = async () => {
    if (filterName.trim()) {
      await onSave({
        name: filterName.trim(),
        description: filterDescription.trim(),
        is_default: setAsDefault,
      });
      // Only reset form after successful save
      setFilterName("");
      setFilterDescription("");
      setSetAsDefault(false);
      setShowAsterisk(true);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFilterName("");
      setFilterDescription("");
      setSetAsDefault(false);
      setShowAsterisk(true);
      onClose();
    }
  };

  // Determine which filters to display
  const filtersToDisplay = existingFilter
    ? existingFilter.filters
    : appliedFilters;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.modal_bg,
          borderRadius: "10px",
          boxShadow: colors.dropdown_menu_shadow,
          width: "379px",
          maxWidth: "379px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 24px 16px 24px",
          color: colors.text_primary,
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "22px",
        }}
      >
        {title}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: colors.text_secondary,
            width: "24px",
            height: "24px",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: colors.action_hover,
            },
          }}
        >
          <Close sx={{ fontSize: "16px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 24px 16px 24px" }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Filter Name Field */}
          <Box sx={{ position: "relative" }}>
            <TextField
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Filter name"
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: 30 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.common_white,
                  minHeight: "44px",
                  paddingLeft: "14px",
                  paddingRight: "8px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  borderRadius: "8px",
                  "& fieldset": {
                    border: `1px solid ${colors.textfield_border}`,
                    borderRadius: "8px",
                  },
                  "&:hover fieldset": {
                    borderColor: colors.textfield_border_hover,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary_main,
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: colors.text_secondary,
                  padding: 0,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.text_secondary,
                  opacity: 1,
                },
              }}
            />
            {showAsterisk && (
              <Typography
                sx={{
                  position: "absolute",
                  right: existingFilter ? "14px" : "auto",
                  left: existingFilter ? "auto" : "86px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "14px",
                  color: colors.error_main,
                  fontWeight: 400,
                  pointerEvents: "none",
                }}
              >
                *
              </Typography>
            )}
          </Box>

          {/* Description Field */}
          <Box>
            <TextField
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              placeholder="Description (optional)"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              inputProps={{ maxLength: 100 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.common_white,
                  minHeight: "44px",
                  paddingLeft: "14px",
                  paddingRight: "8px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  borderRadius: "8px",
                  "& fieldset": {
                    border: `1px solid ${colors.textfield_border}`,
                    borderRadius: "8px",
                  },
                  "&:hover fieldset": {
                    borderColor: colors.textfield_border_hover,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary_main,
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: colors.text_secondary,
                  padding: 0,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.text_secondary,
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Filters Section */}
          <FilterDisplayBox
            filters={filtersToDisplay}
            isEditMode={!!existingFilter}
          />

          {/* Set as Default Switch */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              height: "24px",
            }}
          >
            <ToggleSwitch
              defaultValue={setAsDefault}
              updateFunction={(newValue) => {
                setSetAsDefault(newValue);
                return Promise.resolve();
              }}
              setFetchStatus={() => {}}
            />
            <Typography
              variant="caption"
              sx={{
                color: colors.text_secondary,
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
              }}
            >
              Set as default filter
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 24px 24px", gap: "16px" }}>
        <TextButton
          onClick={handleClose}
          disabled={isLoading}
          color="inherit"
          size="small"
        >
          Cancel
        </TextButton>
        <ContainedButton
          onClick={handleSave}
          disabled={!filterName.trim() || isLoading}
          loading={isLoading}
          color="primary"
          size="small"
        >
          {isLoading ? "Saving..." : saveButtonText}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default BaseFilterModal;
